import React from 'react';
import {
  View,
  ScrollView,
  ActivityIndicator,
  TouchableOpacity,
  TextInput,
} from 'react-native';
import Constants from 'expo-constants';
import ENV from 'app/utils/ENV';
import Text from 'app/components/Text';
import narrativeActions from 'app/utils/narrativeActions';
import valueActions from 'app/utils/valueActions';
import { StyleContext } from 'app/contexts/Style';
import { Feather } from '@expo/vector-icons';

/// Generates a narrative of a single value's history (its text + all its
/// moments' text). After the first pass you can give feedback and ask the AI to
/// revise it, then save the result as a note under the value.
export default class ValueNarrativeModal extends React.Component {
  static contextType = StyleContext;

  state = {
    loading: false,
    narrative: null,
    narrativeTitle: null,
    feedback: '',
    error: null,
    saving: false,
    saved: false,
    useClaude: false,
  };

  // feedback != null => revision pass (sends the prior narrative + feedback).
  generate = async (feedback = null) => {
    const { value } = this.props;
    if (!value?.id) return;
    const { useClaude, narrative } = this.state;
    this.setState({ loading: true, error: null, saved: false });
    const res = await narrativeActions.generateForValue(
      value.id,
      useClaude,
      feedback,
      feedback ? narrative : null,
    );
    if (res?.success) {
      this.setState({ loading: false, narrative: res.narrative, narrativeTitle: res.title, feedback: '' });
    } else {
      this.setState({ loading: false, error: res?.error || 'Something went wrong.' });
    }
  };

  revise = () => {
    const fb = this.state.feedback.trim();
    if (fb) this.generate(fb);
  };

  saveAsNote = async () => {
    const { value } = this.props;
    const { narrative, narrativeTitle } = this.state;
    if (!narrative) return;
    this.setState({ saving: true });
    await valueActions.create({
      title: narrativeTitle || value?.title || 'Narrative',
      text: narrative,
      is_note: true,
      category: 'narration',
      parent_ids: value?.id ? [value.id] : [],
      completed_at: new Date().toISOString(),
    });
    this.setState({ saving: false, saved: true });
    this.props.onSaved?.();
  };

  render() {
    const { onClose, value } = this.props;
    const { loading, narrative, narrativeTitle, feedback, error, saving, saved, useClaude } = this.state;
    const { colors, sizes } = this.context;

    const btn = {
      backgroundColor: colors.trim,
      paddingHorizontal: 18,
      paddingVertical: 10,
      borderRadius: 8,
      alignItems: 'center',
    };

    const panel = (
      <View style={ENV.WEB ? {
        width: 460,
        height: '100%',
        backgroundColor: colors.empty,
        borderLeftWidth: 1,
        borderLeftColor: colors.trim,
        shadowColor: '#000',
        shadowOpacity: 0.25,
        shadowRadius: 16,
      } : {
        flex: 1,
        backgroundColor: colors.empty,
        paddingTop: Constants.statusBarHeight,
      }}>
        <View style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingHorizontal: 20,
          paddingVertical: 14,
          borderBottomWidth: 1,
          borderBottomColor: colors.trim,
        }}>
          <Text bold style={{ fontSize: sizes.large, flex: 1 }} numberOfLines={1}>
            {value?.title ? `Narrative — ${value.title}` : 'Value Narrative'}
          </Text>
          <TouchableOpacity onPress={onClose}>
            <Feather name="x" size={sizes.large} color={colors.foreground} />
          </TouchableOpacity>
        </View>

        <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 20 }}>
          {!narrative && (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 16 }}>
              <TouchableOpacity style={btn} onPress={() => this.generate()} disabled={loading}>
                {loading ? <ActivityIndicator color="#fff" /> : <Text bold style={{ color: '#fff' }}>Generate</Text>}
              </TouchableOpacity>
              <TouchableOpacity
                onPress={() => this.setState({ useClaude: !useClaude })}
                style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}
              >
                <View style={{
                  width: 36, height: 20, borderRadius: 10,
                  backgroundColor: useClaude ? colors.trim : colors.lightForeground,
                  justifyContent: 'center', paddingHorizontal: 2,
                }}>
                  <View style={{ width: 16, height: 16, borderRadius: 8, backgroundColor: '#fff', alignSelf: useClaude ? 'flex-end' : 'flex-start' }} />
                </View>
                <Text style={{ color: colors.foreground }}>Claude</Text>
              </TouchableOpacity>
            </View>
          )}

          {!!error && <Text style={{ color: 'red', marginTop: 12 }}>{error}</Text>}

          {!!narrative && (
            <View>
              {!!narrativeTitle && (
                <Text bold style={{ fontSize: sizes.large, marginBottom: 10, color: colors.foreground }}>
                  {narrativeTitle}
                </Text>
              )}
              <Text style={{ lineHeight: sizes.default * 1.7, color: colors.foreground }}>
                {narrative}
              </Text>

              <Text style={{ color: colors.lightForeground, marginTop: 20, marginBottom: 6 }}>
                Give feedback to revise:
              </Text>
              <TextInput
                placeholder="e.g. make it shorter, focus on the early days…"
                placeholderTextColor={colors.placeholder}
                value={feedback}
                onChangeText={(t) => this.setState({ feedback: t })}
                multiline
                style={{
                  borderWidth: 1,
                  borderColor: colors.trim,
                  borderRadius: 8,
                  padding: 10,
                  minHeight: 60,
                  color: colors.foreground,
                }}
              />
              <View style={{ flexDirection: 'row', gap: 12, marginTop: 10 }}>
                <TouchableOpacity
                  style={[btn, { opacity: (loading || !feedback.trim()) ? 0.5 : 1 }]}
                  onPress={this.revise}
                  disabled={loading || !feedback.trim()}
                >
                  {loading ? <ActivityIndicator color="#fff" /> : <Text bold style={{ color: '#fff' }}>Revise</Text>}
                </TouchableOpacity>
                <TouchableOpacity
                  style={[btn, { opacity: saved ? 0.5 : 1 }]}
                  onPress={this.saveAsNote}
                  disabled={saving || saved}
                >
                  {saving
                    ? <ActivityIndicator color="#fff" />
                    : <Text bold style={{ color: '#fff' }}>{saved ? 'Saved!' : 'Save as Note'}</Text>}
                </TouchableOpacity>
              </View>
            </View>
          )}
        </ScrollView>
      </View>
    );

    // Native: ValueNote wraps this in a fullscreen <Modal>.
    if (!ENV.WEB) return panel;

    // Web: dim backdrop (tap to close) + right-docked panel.
    return (
      <View style={{ flex: 1, flexDirection: 'row', justifyContent: 'flex-end' }}>
        <TouchableOpacity activeOpacity={1} onPress={onClose} style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.25)' }} />
        {panel}
      </View>
    );
  }
}
